<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useNavStore } from '@/shared/stores/NavStore'
import { useAuthStore } from '@/shared/stores/AuthStore'
import { useAdminAccessStore } from '@/shared/stores/AdminAccessStore'
import { useCommandPalette } from '@/shared/composables/UseCommandPalette'
import { resolveDefaultDepartment } from '@/shared/composables/UseMenuPreferences'
import UserAvatar from '@/shared/components/UserAvatar.vue'

type Crumb = {
  label: string
  routeName?: string
}

const route = useRoute()
const router = useRouter()
const navStore = useNavStore()
const { crumbOverride } = storeToRefs(navStore)
const { open: openCommandPalette, shortcutLabel } = useCommandPalette()

const userAuth = useAuthStore()
const { user } = storeToRefs(userAuth)

const adminAccess = useAdminAccessStore()
const department = computed(() => resolveDefaultDepartment(adminAccess.access?.roles ?? []))

/*
 * The trail is derived from route meta: `title` names the page, `breadcrumbParent` points to the
 * parent page's route name (which supplies its own title/parent, recursively). The last crumb is
 * the page title itself — replaced by `crumbOverride` when a detail page sets a dynamic label.
 */
const trail = computed<Crumb[]>(() => {
  const crumbs: Crumb[] = []
  const seen = new Set<string>()
  let parentName = route.meta.breadcrumbParent

  while (parentName && !seen.has(parentName)) {
    seen.add(parentName)

    try {
      const parent = router.resolve({ name: parentName } as any)

      crumbs.unshift({
        label: parent.meta.title ?? parentName,
        routeName: parentName,
      })
      parentName = parent.meta.breadcrumbParent
    } catch {
      break
    }
  }

  crumbs.push({ label: crumbOverride.value ?? route.meta.title ?? '' })

  return crumbs
})
</script>

<template>
  <section class="h-14 flex shrink-0 items-center gap-x-2 overflow-hidden border-b border-surface-100 bg-surface-0 px-4 sm:px-6">
    <template v-for="(crumb, index) in trail" :key="index">
      <template v-if="index < trail.length - 1">
        <router-link
          :to="{ name: crumb.routeName as never }"
          class="whitespace-nowrap text-base color-surface-500 font-medium no-underline transition-colors hover:color-primary"
        >
          {{ crumb.label }}
        </router-link>
        <i class="fa-regular fa-chevron-right text-xs color-surface-400" aria-hidden="true" />
      </template>
      <h3 v-else class="min-w-0 truncate text-base" :title="crumb.label">
        {{ crumb.label }}
      </h3>
    </template>

    <Button
      unstyled
      type="button"
      class="ml-auto flex shrink-0 cursor-pointer items-center gap-x-2 border border-surface-200/50 rounded-full bg-surface-100 px-3 py-2 text-sm color-surface-500 transition-colors hover:bg-surface-200 hover:color-surface-700"
      aria-label="Zoeken"
      @click="openCommandPalette"
    >
      <i class="fa-regular fa-search" aria-hidden="true" />
      <span class="hidden sm:inline">Zoeken</span>
      <kbd class="hidden items-center border border-surface-200 rounded-md bg-surface-0 px-1.5 py-0.5 text-xs color-surface-400 sm:flex">
        {{ shortcutLabel }}
      </kbd>
    </Button>

    <!-- User identity: hidden on mobile (handled by MobileMenu) -->
    <div v-if="user" class="hidden items-center gap-x-2.5 sm:flex">
      <UserAvatar :name="user.name ?? ''" class="h-8 w-8 text-xs" />
      <div class="flex flex-col leading-tight">
        <span class="whitespace-nowrap text-sm color-primary font-medium">{{ user.name }}</span>
        <span v-if="department" class="whitespace-nowrap text-xs color-surface-400">{{ department }}</span>
      </div>
    </div>
  </section>
</template>
